import {useState} from "react";
import {StatSectionCard} from "./stats/StatSectionCard";
import {useTranslation} from "../translations/LanguageContext";
import {Accordion} from "./stats/Accordion";
import {ProgressBar} from "./stats/ProgressBar";

// ----- COLOURS -----
const C = [
    "#4f8ef7", // blue
    "#f7924f", // orange
    "#4fcf8e", // green
    "#f74f6e", // red
    "#a64ff7", // purple
    "#f7d44f", // yellow
    "#4fd4f7", // cyan
    "#f74fca", // pink
];

// Converts a ratio (0–1) to a rounded percentage
function toPercent(ratio) {
    if (ratio === null || ratio === undefined) return 0;
    return Math.round(ratio * 100);
}

// Formats a number to a given number of decimal places
function fmt(value, decimals = 2) {
    if (value === null || value === undefined) return "-";
    return typeof value === "number" ? value.toFixed(decimals) : value;
}

export function ResultsBoxEnglish({ data, isOutdated }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("overview");
    const [openAccordion, setOpenAccordion] = useState(null);

    if (!data) return (
        <article className="results-box">
            <div className="input-top input-section">
                <p>{t("resultsBox.analysisResult")}</p>
            </div>
            <p className="results-empty">{t("resultsBox.empty")}</p>
        </article>
    );

    const r = data.results;

    /*
     * Keep English labels in the translation files so the component can
     * follow the same translation structure as the Welsh ResultsBox.
     */
    const rb = {
        overview: t("resultsBox.overview"),
        vocabulary: t("resultsBox.vocabulary"),
        grammar: t("resultsBox.grammar"),
        structure: t("resultsBox.structure"),
    };

    const tabsDict = t("resultsBox.tabs");
    const tabs = [["overview", tabsDict.overview], ["vocabulary", tabsDict.vocabulary], ["grammar", tabsDict.grammar], ["structure", tabsDict.structure],];


    const cefrLevels = ["A1", "A2", "B1", "B2", "C1", "C2"];
    const kBands = ["K1", "K2", "K3", "K4", "K5", "K6+"];

    return (
        <article className="results-box">

            {isOutdated && (
                <div className="analysis-warning">
                    {t("resultsBox.outdatedWarning")}
                </div>
            )}

            <div className="button-wrap">
                {tabs.map(([key, label]) => (
                    <button
                        key={key}
                        className={`toggle-btn ${activeTab === key ? "toggle-btn--active" : ""}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* ── Overview ── */}
            {activeTab === "overview" && (
                <section className="tab-section">
                    <p className="section-label">{rb.overview.textSizeSection}</p>

                    <div className="stat-gallery">
                        <StatSectionCard
                            name={rb.overview.totalWordsName}
                            value={r["Word Count"]}
                            label={rb.overview.totalWordsLabel}
                        />

                        <StatSectionCard
                            name={rb.overview.sentencesName}
                            value={r["Sentence Count"]}
                            label={rb.overview.sentencesLabel}
                        />

                        <StatSectionCard
                            name={rb.overview.avgSentenceLengthName}
                            value={fmt(r["Average Sentence Length"])}
                            label={rb.overview.avgSentenceLengthLabel}
                        />
                    </div>

                    <p className="section-label">{rb.vocabulary.diversityName}</p>

                    <div className="stat-gallery">
                        <StatSectionCard
                            name={rb.vocabulary.diversityLabel}
                            value={fmt(r["TTR (Type Token Ratio)"])}
                            label={rb.vocabulary.diversityLabel}
                        />
                    </div>
                </section>
            )}

            {/* ── Vocabulary ── */}
            {activeTab === "vocabulary" && (
                <section className="tab-section">
                    <Accordion
                        title={rb.vocabulary.cefrAccordion.title}
                        open={openAccordion === "cefr"}
                        onToggle={() =>
                            setOpenAccordion(current =>
                                current === "cefr" ? null : "cefr"
                            )
                        }
                    >
                        <div className="progress-stack">
                            {cefrLevels.map((lvl, i) => (
                                <ProgressBar
                                    key={lvl}
                                    label={lvl}
                                    value={toPercent(r[`${lvl} - ratio to all words`])}
                                    colour={C[i % C.length]}
                                />
                            ))}
                        </div>
                    </Accordion>

                    <Accordion
                        title={rb.vocabulary.frequencyAccordion.title}
                        open={openAccordion === "frequency"}
                        onToggle={() =>
                            setOpenAccordion(current =>
                                current === "frequency" ? null : "frequency"
                            )
                        }
                    >
                        <div className="progress-stack">
                            {kBands.map((band, i) => (
                                <ProgressBar
                                    key={band}
                                    label={band}
                                    value={toPercent(r[`${band} - ratio to all words`])}
                                    colour={C[i % C.length]}
                                />
                            ))}
                        </div>
                    </Accordion>

                    <Accordion
                        title={rb.vocabulary.stopwordsAccordion.title}
                        open={openAccordion === "stopwords"}
                        onToggle={() =>
                            setOpenAccordion(current =>
                                current === "stopwords" ? null : "stopwords"
                            )
                        }
                    >
                        <div className="stat-gallery">
                            <StatSectionCard
                                name={rb.vocabulary.stopwordsAccordion.functionWordsName}
                                value={`${toPercent(
                                    r["stopwords - ratio to all words"]
                                )}%`}
                                label={rb.vocabulary.stopwordsAccordion.functionWordsLabel}
                            />

                            <StatSectionCard
                                name={rb.vocabulary.stopwordsAccordion.uniqueFunctionWordsName}
                                value={r["unique stopwords"]}
                                label={rb.vocabulary.stopwordsAccordion.uniqueFunctionWordsLabel}
                            />
                        </div>
                    </Accordion>
                </section>
            )}

            {/* ── Grammar ── */}
            {activeTab === "grammar" && (
                <section className="tab-section">
                    <p className="section-label">{rb.grammar.posSection}</p>

                    <div className="progress-stack">
                        <ProgressBar
                            label={rb.grammar.pos.nouns}
                            value={r["Number of Nouns"]}
                            max={r["Word Count"]}
                            colour={C[0]}
                        />

                        <ProgressBar
                            label={rb.grammar.pos.verbs}
                            value={r["Number of Verbs"]}
                            max={r["Word Count"]}
                            colour={C[1]}
                        />

                        <ProgressBar
                            label={rb.grammar.pos.adjectives}
                            value={r["Number of Adjectives"]}
                            max={r["Word Count"]}
                            colour={C[2]}
                        />

                        <ProgressBar
                            label={rb.grammar.pos.adverbs}
                            value={r["Number of Adverbs"]}
                            max={r["Word Count"]}
                            colour={C[3]}
                        />

                        <ProgressBar
                            label={rb.grammar.pos.prepositions}
                            value={r["Number of Prepositions"]}
                            max={r["Word Count"]}
                            colour={C[4]}
                        />

                        <ProgressBar
                            label={rb.grammar.pos.conjunctions}
                            value={r["Number of Conjunctions"]}
                            max={r["Word Count"]}
                            colour={C[5]}
                        />

                        <ProgressBar
                            label={t("textAnnotator.pos.determiner")}
                            value={r["Number of Determiners"]}
                            max={r["Word Count"]}
                            colour={C[6]}
                        />

                        <ProgressBar
                            label={rb.grammar.pos.pronouns}
                            value={r["Number of Pronouns"]}
                            max={r["Word Count"]}
                            colour={C[7]}
                        />
                    </div>

                    <p className="section-label">Noun forms</p>

                    <div className="occurrence-list">
                        <div className="occurrence-row">
                            <span>Singular nouns</span>
                            <span>{r["Number of Singular nouns"]}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>Plural nouns</span>
                            <span>{r["Number of Plural nouns"]}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>Interjections</span>
                            <span>{r["Number of Interjections"]}</span>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Structure ── */}
            {activeTab === "structure" && (
                <section className="tab-section">
                    <p className="section-label">
                        {rb.structure.complexitySection}
                    </p>

                    <div className="occurrence-list">
                        <div className="occurrence-row">
                            <span>{rb.structure.headDistance}</span>
                            <span>{fmt(r["Average head distance"])}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.avgTreeDepth}</span>
                            <span>{fmt(r["Average depth"])}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.maxTreeDepth}</span>
                            <span>{r["Max Tree depth"]}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.branchingFactor}</span>
                            <span>{fmt(r["Max branching factor"])}</span>
                        </div>
                    </div>

                    <p className="section-label">
                        {rb.structure.clauseSection}
                    </p>

                    <div className="occurrence-list">
                        <div className="occurrence-row">
                            <span>{rb.structure.subjectRatio}</span>
                            <span>{fmt(r["Subject ratio"])}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.leftRightRatio}</span>
                            <span>{fmt(r["Left-right branching ratio"])}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.clausesPerSentence}</span>
                            <span>{fmt(r["Clause count per sentence"])}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.totalClauses}</span>
                            <span>{r["Total clause count"]}</span>
                        </div>

                        <div className="occurrence-row">
                            <span>{rb.structure.auxiliaryVerbs}</span>
                            <span>{r["Number of auxiliaries"]}</span>
                        </div>
                    </div>
                </section>
            )}

        </article>
    );
}